import React, { useEffect, useState } from 'react';
import ReactTooltip from 'react-tooltip';
import TimeAgo from 'javascript-time-ago';
import en from 'javascript-time-ago/locale/en.json';
import ReactTimeAgo from 'react-time-ago';

import { Avatar } from '@uikit/Avatar/Avatar.stories';
import { Tr } from '@uikit/old/Table/Table';
import ColoredStatusToggler from '@uikit/ColoredStatusToggler/ColoredStatusToggler';
import { SVGIcon, SvgIconType } from '@uikit/old/Icon/Icon';

import { PublishersOrderType } from '@ts/publishers.types';
import { Button } from '@uikit/Form/Button/Button';
import TimestampToDate from '@uikit/TimestampToDate/TimestampToDate';
import ActionsMenu from '@components/ActionsMenu/ActionsMenu';
import { getDateShort } from '@utils/date';
import { PublisherOrderStatus, PublisherOrderType } from 'respona_api/generated/common_pb';
import ConcernStatusLabel, {
  isActiveConcernStatus,
} from '@components/Publisher/Orders/OrdersTable/_components/ConcernStatusLabel/ConcernStatusLabel';
import {
  COPIED_LABEL_TIMEOUT_MS,
  INSERTION_BRIEF_HINT,
  INSERTION_BRIEF_TITLE,
  hasChangeRequest,
} from '@components/Publisher/Orders/OrdersTable/constants';
import { getInsertionBriefUrl } from '@components/Publisher/Orders/OrdersTable/utils';
import { GREY_COLOR, WARNING_COLOR } from '@constants/colors';

// addDefaultLocale() may run only once per page (RES-7334); both Publisher tables register it.
TimeAgo.addLocale(en);
TimeAgo.setDefaultLocale(en.locale);

type TableRenderProps = {
  row: PublishersOrderType;
  children?: any;
  renderProps?: {
    [key: string]: any;
  };
};

export const orderStatusToStringMapper = {
  0: 'TODO',
  1: 'Pending approval',
  2: 'Revision requested',
  3: 'Rejected',
  4: 'Pending publication',
  5: 'Live',
  6: 'Live',
  7: 'Indexed',
  8: 'Paid',
};

export const orderStatusColor = {
  0: '',
  1: '#475D7F',
  4: '#221CB6',
  3: '#ED483D',
  2: '#A3B2C9',
  5: '#F5F6FA',
  6: '#F5F6FA',
  7: '#23E771',
  8: '#23E771',
};

const orderStatusBgColor = {
  0: '',
  1: '#F5F6FA',
  4: '#DEE4FF',
  3: '#FFD8D8',
  2: '#F5F6FA',
  5: '#221CB6',
  6: '#221CB6',
  7: '#ECFFF3',
  8: '#ECFFF3',
};

const orderStatusIcon = {
  0: 'pencil',
  1: null,
  2: 'eyeInCircle',
  3: 'cancelCircle',
  4: 'threeDotsInCircle',
  5: 'playArrowInCircle',
  6: 'playArrowInCircle',
  7: 'checkCircled',
  8: 'successCircle',
} as Record<number, SvgIconType>;

export const orderStatusTextColor = {
  0: '#A3B2C9',
  1: '#FFFFFF',
  2: '#23E771',
  3: '#23E771',
  4: '#A3B2C9',
  5: '#373E48',
  6: '#373E48',
  7: '#475D7F',
  8: '#475D7F',
};

const orderStatusIconColor = {
  0: '#A3B2C9',
  1: '#221CB6',
  2: '#23E771',
  3: '#23E771',
  4: '#A3B2C9',
  5: '#6C7B92',
  6: '#6C7B92',
  7: '#6C7B92',
  8: '#6C7B92',
};

/**
 * The order number is the identifier support and notification emails refer to,
 * so it lives in the row's identity cell rather than in a column of its own.
 * Clicking it copies the raw number so it can be pasted straight into a search.
 */
function OrderNumber({ number }: { number: string }): JSX.Element {
  const [isCopied, setIsCopied] = useState(false);

  useEffect(() => {
    if (!isCopied) {
      return undefined;
    }

    const timeoutId = setTimeout(() => setIsCopied(false), COPIED_LABEL_TIMEOUT_MS);

    return () => clearTimeout(timeoutId);
  }, [isCopied]);

  const handleCopy = (event: React.SyntheticEvent<HTMLSpanElement>) => {
    event.stopPropagation();

    if (!navigator.clipboard) {
      return;
    }

    navigator.clipboard
      .writeText(number)
      .then(() => setIsCopied(true))
      .catch(() => setIsCopied(false));
  };

  return (
    <span
      className="orders-table-website__number"
      onClick={handleCopy}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          handleCopy(event);
        }
      }}
      title="Click to copy order number"
    >
      #{number}
      {isCopied ? (
        <span className="orders-table-website__number-copied">Copied</span>
      ) : (
        <SVGIcon icon="copy" size={10} color={GREY_COLOR} />
      )}
    </span>
  );
}

function Status({ title, color, bgColor, icon, onClick, status, orderId }) {
  return (
    <div
      className="status-label"
      style={{ backgroundColor: bgColor }}
      data-testid={`publisher-orders-row-status-${orderId}`}
    >
      <SVGIcon icon={icon} color={color} size={12} />
      <span style={{ color }} data-testid={`publisher-orders-row-status-label-${orderId}`}>
        {title}
      </span>
      {status === PublisherOrderStatus.PENDING_PUBLICATION ? (
        <Button
          className="link-button"
          type="bordered-grey"
          onClick={() => onClick(PublisherOrderStatus.PUBLISHER_ORDER_PENDING_LIVE)}
          data-testid={`publisher-orders-row-submit-url-${orderId}`}
        >
          Add URL
        </Button>
      ) : null}
      {status === PublisherOrderStatus.PENDING_PUBLISHER_APPROVAL ? (
        <div className="status-label-container">
          <div
            onClick={() => onClick(PublisherOrderStatus.PENDING_PUBLICATION)}
            data-testid={`publisher-orders-row-accept-${orderId}`}
          >
            <SVGIcon icon="checkCircled" color="#23E771" size={18} />
          </div>
          <div
            onClick={() => onClick(PublisherOrderStatus.PUBLISHER_ORDER_REJECTED)}
            data-testid={`publisher-orders-row-reject-${orderId}`}
          >
            <SVGIcon icon="crossDelete" color="#ED483D" size={22} />
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default [
  {
    title: 'Website',
    dataKey: 'website',
    RenderCell: ({ row }: TableRenderProps): JSX.Element => {
      let parsedURL = row.websiteDomain;

      if (!parsedURL.startsWith('http://') && !parsedURL.startsWith('https://')) {
        parsedURL = `https://${parsedURL}`;
      }

      return (
        <div className="title-cell">
          <div className="title-cell__body orders-table-website row-direction">
            <Avatar isSquared url={row.websiteLogo} fullName={row.websiteName} size="xs" />
            <div className="title-cell__title" title={row.websiteDomain}>
              <p>{row.websiteName}</p>
              <a
                key={row.websiteId}
                target="_blank"
                rel="noopener noreferrer"
                href={parsedURL}
                className="link"
              >
                {row.websiteDomain}
              </a>
              {row.number ? <OrderNumber number={row.number} /> : null}
            </div>
          </div>
        </div>
      );
    },
  },
  {
    title: 'Type',
    dataKey: 'type',
    RenderCell: ({ row }: TableRenderProps): JSX.Element => {
      const isGuestPost = row.type === PublisherOrderType.GUEST_POST;
      const label = isGuestPost ? 'Guest Post' : 'Link Insertion';
      const color = isGuestPost ? '#221CB6' : '#FC8619';
      const bgColor = isGuestPost ? '#DEE4FF' : '#FFF2E5';

      return (
        <div
          className="status-label"
          style={{ backgroundColor: bgColor }}
          data-testid={`publisher-orders-row-type-${row.id}`}
        >
          <span style={{ color }}>{label}</span>
        </div>
      );
    },
  },
  {
    title: 'Article',
    dataKey: 'article',
    RenderCell: ({ row }: TableRenderProps): JSX.Element => {
      const isGuestPost = row.type === PublisherOrderType.GUEST_POST;
      const title = isGuestPost ? row.title : row.linkInsertionArticleTitle;
      const url = isGuestPost ? row.documentUrl : row.linkInsertionArticleUrl;
      const comment = !isGuestPost ? row.linkInsertionComment : '';
      // Pipeline link insertions carry a Google Doc brief in documentUrl until
      // the order is live; it is shown next to the article URL, never instead of it.
      const briefUrl = getInsertionBriefUrl(row);

      let parsedURL = url;

      if (parsedURL && !parsedURL.startsWith('http://') && !parsedURL.startsWith('https://')) {
        parsedURL = `https://${parsedURL}`;
      }

      return (
        <div className="orders-table-article" data-tip data-for={`article-comment-${row.id}`}>
          <p>{title}</p>
          <div className="orders-table-icons">
            {url && (
              <a
                key={row.id}
                href={parsedURL}
                target="_blank"
                rel="noopener noreferrer"
                className="link orders-table__link"
                data-testid={`publisher-orders-row-article-url-${row.id}`}
              >
                <SVGIcon icon="linkSingleChain" /> {url}
              </a>
            )}
            {briefUrl && (
              <a
                href={briefUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="link orders-table__link orders-table__brief-link"
                data-testid={`publisher-orders-row-brief-${row.id}`}
                title={INSERTION_BRIEF_HINT}
              >
                <SVGIcon icon="documentIcon" color={WARNING_COLOR} /> {INSERTION_BRIEF_TITLE}
              </a>
            )}
          </div>
          {comment ? (
            <ReactTooltip
              className="orders-table-tooltip"
              id={`article-comment-${row.id}`}
              place="top"
              effect="solid"
              arrowColor="transparent"
            >
              {comment}
            </ReactTooltip>
          ) : null}
        </div>
      );
    },
  },
  {
    title: '# of links',
    dataKey: 'ofLinks',
    RenderCell: ({ row }: TableRenderProps): JSX.Element => {
      return (
        <div className="orders-table-of-links">
          <ColoredStatusToggler
            id={row.id}
            name="Order"
            title="view"
            color={orderStatusColor[5]}
            titleColor={orderStatusTextColor[5]}
            iconColor={orderStatusIconColor[5]}
            iconSize={10}
            isActive={false}
            clickable={false}
            isView
            nameString={`${row.backlinksList.length}`}
          />
          {row.backlinksList.length > 0 ? (
            <ReactTooltip
              className="orders-table-tooltip"
              id={`colored-status-button-${row.id}`}
              place="top"
              effect="solid"
              arrowColor="transparent"
              delayHide={200}
            >
              {row.backlinksList.map((link, index) => (
                <div
                  // eslint-disable-next-line react/no-array-index-key
                  key={`${link.url}-${link.anchor}-${index}`}
                  className="orders-table-tooltip__body"
                >
                  <div style={{ color: '#8D8F92', fontSize: '10px' }}>Target URL:</div>
                  <div style={{ color: '#8782FF', marginBottom: '13px' }}>{link.url}</div>
                  <div style={{ color: '#8D8F92', fontSize: '10px' }}>Anchor:</div>
                  <div>{link.anchor}</div>
                </div>
              ))}
            </ReactTooltip>
          ) : null}
        </div>
      );
    },
  },
  {
    title: 'Status',
    dataKey: 'statusToggler',
    RenderCell: ({
      row,
      renderProps: { onUpdateStatus, onViewChangeRequest },
    }: TableRenderProps): JSX.Element => {
      let ColorLabel = null;

      // An active concern overrides the regular placement lifecycle status
      // until it is resolved
      if (isActiveConcernStatus(row.optionalChangeRequestStatus)) {
        ColorLabel = (
          <ConcernStatusLabel
            status={row.optionalChangeRequestStatus}
            onView={() => onViewChangeRequest(row)}
            testId={`publisher-orders-row-concern-status-${row.id}`}
          />
        );
      } else {
        ColorLabel = (
          <Status
            title={orderStatusToStringMapper[row.status]}
            color={orderStatusColor[row.status]}
            bgColor={orderStatusBgColor[row.status]}
            icon={orderStatusIcon[row.status]}
            status={row.status}
            orderId={row.id}
            onClick={(status) => {
              onUpdateStatus(row.id, status);
            }}
          />
        );
      }

      return ColorLabel;
    },
  },
  {
    title: 'Price',
    dataKey: 'price',
    RenderCell: ({ row }: TableRenderProps): JSX.Element => (
      <div className="total-cell">
        <div
          data-tip
          data-for={`price-comment-${row.id}`}
          data-testid={`publisher-orders-row-price-${row.id}`}
        >
          ${row.price}
        </div>
        {row.priceComment ? (
          <ReactTooltip
            className="orders-table-tooltip"
            id={`price-comment-${row.id}`}
            place="top"
            effect="solid"
            arrowColor="transparent"
          >
            {row.priceComment}
          </ReactTooltip>
        ) : null}
      </div>
    ),
  },
  {
    title: 'Deadline',
    dataKey: 'deadlineAt',
    RenderCell: ({ row }: TableRenderProps): JSX.Element => {
      const now = new Date();
      const time = new Date(row.deadlineAt);

      // time.setDate(20);
      // time.setMonth(11);

      const isPast = time < now;
      const oneDay = 24 * 60 * 60 * 1000;
      const inOneDay = time.getTime() > new Date().getTime() + oneDay;
      const inThreeDays = time.getTime() > new Date().getTime() + 3 * oneDay;
      const inFourDays = time.getTime() > new Date().getTime() + 4 * oneDay;
      const isShownDayCounter =
        row.status === PublisherOrderStatus.PENDING_PUBLICATION ||
        row.status === PublisherOrderStatus.PENDING_PUBLISHER_APPROVAL ||
        row.status === PublisherOrderStatus.REVISION_REQUESTED;

      let color = '';

      if (inFourDays) {
        color = '';
      } else if (inThreeDays) {
        color = '#fc8619';
      } else if (inOneDay || isPast) {
        color = '#ED483D';
      }

      return (
        <div className="created_on-cell">
          {getDateShort(time, true)}{' '}
          {isShownDayCounter && (
            <p style={{ color }} data-testid={`publisher-orders-row-deadline-counter-${row.id}`}>
              (
              {isPast ? (
                <ReactTimeAgo date={time} locale="en-US" future={false} />
              ) : (
                <ReactTimeAgo date={time} locale="en-US" future />
              )}
              )
            </p>
          )}
        </div>
      );
    },
  },
  {
    title: 'Requested',
    dataKey: 'requested',
    RenderCell: ({ row }: TableRenderProps): JSX.Element =>
      row.requestedAt !== -1 ? <TimestampToDate timestamp={row.requestedAt} /> : <span>N/A</span>,
  },
  {
    title: '',
    dataKey: 'actions',
    thCellClassName: 'orders-table__actions-cell',
    cellClassName: 'orders-table__actions-cell',
    RenderCell: ({
      row,
      renderProps: { onUpdateStatus, onEditUrl, onViewChangeRequest },
    }: TableRenderProps): JSX.Element => {
      const actionsConfig = [];

      if (
        row.status === PublisherOrderStatus.REVISION_REQUESTED ||
        row.status === PublisherOrderStatus.PENDING_PUBLICATION
      ) {
        actionsConfig.push({
          icon: 'trashWhite' as SvgIconType,
          title: 'Reject order',
          handler: () => onUpdateStatus(row.id, PublisherOrderStatus.PUBLISHER_ORDER_REJECTED),
          testId: `publisher-orders-row-menu-reject-${row.id}`,
        });
      }

      if (row.status === PublisherOrderStatus.PUBLISHER_ORDER_PENDING_LIVE) {
        // From Pending Live on, `documentUrl` is the submitted live URL for both order types
        // (GrpcMapper.toPublisherOrderResponse). A link insertion's article URL is only the
        // fallback for rows written before the submitted URL was carried there.
        const isGuestPost = row.type === PublisherOrderType.GUEST_POST;
        const currentUrl = isGuestPost
          ? row.documentUrl
          : row.documentUrl || row.linkInsertionArticleUrl;
        actionsConfig.push({
          icon: 'pencil' as SvgIconType,
          title: 'Edit live URL',
          handler: () => onEditUrl(row.id, currentUrl),
          testId: `publisher-orders-row-edit-url-${row.id}`,
        });
      }

      // Resolved change requests stay openable so publishers can read the history back.
      if (hasChangeRequest(row.optionalChangeRequestStatus)) {
        actionsConfig.push({
          icon: 'renameAction' as SvgIconType,
          title: 'View change request',
          handler: () => onViewChangeRequest(row),
          testId: `publisher-orders-row-view-change-request-${row.id}`,
        });
      }

      if (actionsConfig.length === 0) {
        return null;
      }

      return (
        <ActionsMenu
          actionsConfig={actionsConfig}
          id={`website-actions-${row.id}`}
          tooltipPlace="left"
          testId={`publisher-orders-row-actions-${row.id}`}
        />
      );
    },
  },
];

export function RenderRow({
  children,
  rowTestId,
}: TableRenderProps & { rowTestId?: string }): JSX.Element {
  return (
    <Tr className="orders-table__row" data-testid={rowTestId}>
      {children}
    </Tr>
  );
}
